import type { PlanProjectionGroup } from '@developerz/domain';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type PlanDagEdge, type PlanDagNode, planDagNodes } from '../lib/plan-dag';
import { StatusPill, type StatusTone } from './StatusPill';

// The plan DAG rail (#4341) — groups in `(position, groupId)` order (`planDagNodes`
// already resolves that ordering, the humanised hold text and each node's incoming edges),
// rendered left-to-right. Presenter only: <PlanBreakdown/> and <FleetEpicPanel/> own the
// fetch; this takes a plan's groups and shapes nothing itself. Renders only at 2+ groups —
// the caller's call, not this component's (77% of plans have exactly one).
//
// EDGES ARE DRAWN INTO THEIR NODE (plan 101 slice 12), one row per incoming edge, because
// plan order is not the graph: a hairline between consecutive nodes claimed a chain that
// a fan-in or a cross-repo goal does not have. Each edge row is styled by BOTH axes —
// `release` is the line (merged solid, deployed dashed), `hold` is the end (start a bar,
// merge a ring) — and says both in words ("after X deploys to production · holds start"),
// so neither colour nor line style is ever the only signal.
//
// COLOUR IS NEVER THE ONLY SIGNAL (house rule + WCAG 1.4.1): each node's
// border-colour modifier is paired with a `<StatusPill>` TEXT label, so a
// colour-blind or monochrome reader still tells landed from stalled from
// in-flight — the same pairing `styles/views/_fleet.scss`'s `.plan-dag__node`
// comment calls for ("landed and stalled reuse the same hues <StatusPill>
// already carries").
//
// `fleet.plans.custom.disposition.*` is the SAME key family
// `PlanGroupTable.tsx`'s status pill reads — one vocabulary across both
// breakdown surfaces, curated over `PlanGroupDisposition`
// (`packages/domain/src/value-objects/plan-projection.ts`).
//
// ONLY TWO modifiers exist on `.plan-dag__node` for disposition itself —
// `--landed` and `--stalled` — `in_flight` is the unmodified default
// appearance, and `--held` is an INDEPENDENT overlay (dashed border) applied
// whenever the group carries an active hold, regardless of disposition (a
// held group's disposition is always `in_flight` per `PlanGroupHoldSchema`'s
// docblock — waiting to start is not the same fact as being unable to ever
// finish).

const DISPOSITION_TONE: Record<PlanDagNode['disposition'], StatusTone> = {
  landed: 'success',
  // Released like `landed`, but nothing shipped — a success in tone would claim it did.
  no_work: 'warning',
  in_flight: 'neutral',
  stalled: 'danger',
  // Plan 101 D7: a repair replaced the slice. Not a failure to act on; its replacement is.
  superseded: 'neutral',
};

function dispositionLabel(disposition: PlanDagNode['disposition']): string {
  return t(`fleet.plans.custom.disposition.${disposition}`);
}

/** One incoming edge's words: how it releases, what it holds, and whether it has released. */
function edgeLine(edge: PlanDagEdge): string {
  const vars = { upstream: edge.upstreamTitle, environment: edge.environment ?? '' };
  const release =
    edge.release === 'merged'
      ? t('fleet.plans.dag.edge_merged', vars)
      : t('fleet.plans.dag.edge_deployed', vars);
  const hold =
    edge.hold === 'start' ? t('fleet.plans.dag.hold_start') : t('fleet.plans.dag.hold_merge');
  return edge.released
    ? t('fleet.plans.dag.line_released', { edge: release, hold })
    : t('fleet.plans.dag.line', { edge: release, hold });
}

export type PlanDagRailProps = {
  readonly groups: readonly PlanProjectionGroup[];
};

export function PlanDagRail(props: PlanDagRailProps): JSX.Element {
  const nodes = (): PlanDagNode[] => planDagNodes(props.groups);

  return (
    <div class="plan-dag">
      <For each={nodes()}>
        {(node) => (
          <div
            class="plan-dag__node"
            classList={{
              'plan-dag__node--landed': node.disposition === 'landed',
              'plan-dag__node--stalled': node.disposition === 'stalled',
              'plan-dag__node--held': node.hold !== null,
            }}
          >
            <span class="plan-dag__node-title">{node.title}</span>
            <StatusPill tone={DISPOSITION_TONE[node.disposition]}>
              {dispositionLabel(node.disposition)}
            </StatusPill>
            <Show when={node.hold}>
              {(hold) => (
                <span class="plan-dag__node-hold">
                  {t(hold().labelKey, { upstream: hold().upstreamTitle ?? '' })}
                </span>
              )}
            </Show>
            <Show when={node.edges.length > 0}>
              <ul class="plan-dag__edges">
                <For each={node.edges}>
                  {(edge) => (
                    <li
                      class="plan-dag__in"
                      classList={{
                        'plan-dag__in--merged': edge.release === 'merged',
                        'plan-dag__in--deployed': edge.release === 'deployed',
                        'plan-dag__in--hold-start': edge.hold === 'start',
                        'plan-dag__in--hold-merge': edge.hold === 'merge',
                        'plan-dag__in--released': edge.released,
                      }}
                    >
                      <span class="plan-dag__stroke" aria-hidden="true" />
                      <span>{edgeLine(edge)}</span>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </div>
        )}
      </For>
    </div>
  );
}
